// src/components/Post/Post.js
import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { deletePost } from "../../actions/postActions";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css"; // This is important for styling
import "./Post.css";
import ThreeModelViewer from "../ThreeModelViewer";

const extractYouTubeID = (url) => {
  const regex =
    /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/;
  const match = url?.match(regex);
  if (match) {
    console.log(`Extracted YouTube ID: ${match[1]}`);
    return match[1];
  }
  return null;
};

// Reusable iFrame wrapper
const IframeWrapper = ({ src, label, isFullscreen }) => {
  // In fullscreen, we can remove the padding-bottom ratio
  // and force height: 100% (or 100vh) instead
  return (
    <div style={{ padding: isFullscreen ? 0 : "0 1rem" }}>
      <h4 style={{ marginBottom: "0.5rem", textAlign: "left" }}>{label}</h4>
      <div
        style={{
          position: "relative",
          width: "100%",
          // If not fullscreen, keep 16:9 ratio
          // If fullscreen, fill the screen’s height
          paddingBottom: isFullscreen ? "0" : "28.12%",
          height: isFullscreen ? "100vh" : "auto",
          overflow: "hidden",
        }}
      >
        <iframe
          src={src}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            border: "none",
          }}
          allowFullScreen
          title={label}
        />
      </div>
    </div>
  );
};

const Post = ({ post }) => {
  const dispatch = useDispatch();

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  // Track fullscreen state
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Reference to slider container
  const sliderContainerRef = useRef(null);

  const videoId = extractYouTubeID(post.videoURL);

  // Prepare slides
  const carouselSlides = [];
  if (videoId) {
    carouselSlides.push({
      label: "Aerial Circle",
      src: `https://www.youtube.com/embed/${videoId}`,
    });
  }
  if (post.modelURL) {
    carouselSlides.push({ label: "3D Model", src: post.modelURL });
  }
  if (post.walkthroughURL) {
    carouselSlides.push({ label: "360 Walkthrough", src: post.walkthroughURL });
  }

  // Snapshots
  const snapshots = post.snapshots || [];

  const handleDelete = () => {
    dispatch(deletePost(post._id));
  };

  // Slick settings
  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  // Lightbox controls
  const openLightbox = (idx) => {
    setPhotoIndex(idx);
    setLightboxOpen(true);
  };
  const nextSnapshot = () => {
    setPhotoIndex((prev) => (prev + 1) % snapshots.length);
  };
  const prevSnapshot = () => {
    setPhotoIndex((prev) => (prev + snapshots.length - 1) % snapshots.length);
  };

  // Request fullscreen
  const handleEnterFullScreen = () => {
    if (!sliderContainerRef.current) return;
    if (sliderContainerRef.current.requestFullscreen) {
      sliderContainerRef.current.requestFullscreen();
    } else if (sliderContainerRef.current.webkitRequestFullscreen) {
      sliderContainerRef.current.webkitRequestFullscreen();
    } else if (sliderContainerRef.current.mozRequestFullScreen) {
      sliderContainerRef.current.mozRequestFullScreen();
    } else if (sliderContainerRef.current.msRequestFullscreen) {
      sliderContainerRef.current.msRequestFullscreen();
    }
  };

  // Listen for fullscreen changes
  useEffect(() => {
    const handleChange = () => {
      if (
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      ) {
        setIsFullscreen(true);
      } else {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleChange);
    document.addEventListener("webkitfullscreenchange", handleChange);
    document.addEventListener("mozfullscreenchange", handleChange);
    document.addEventListener("MSFullscreenChange", handleChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleChange);
      document.removeEventListener("webkitfullscreenchange", handleChange);
      document.removeEventListener("mozfullscreenchange", handleChange);
      document.removeEventListener("MSFullscreenChange", handleChange);
    };
  }, []);

  // Optional: Dynamically style the container in fullscreen
  const containerStyle = isFullscreen
    ? {
        position: "relative",
        width: "100%",
        height: "100%",
        backgroundColor: "#000", // optional
        overflow: "hidden",
      }
    : {
        position: "relative",
        width: "100%",
        margin: "auto",
      };

  return (
    <div style={{ border: "1px solid #ccc", padding: "1rem", margin: "1rem" }}>
      {/* Slider container */}
      {carouselSlides.length > 0 && (
        <div ref={sliderContainerRef} style={containerStyle}>
          <Slider {...settings}>
            {carouselSlides.map((item, idx) => (
              <div key={idx}>
                {item.label === "3D Model" &&
                item.src?.toLowerCase().endsWith(".glb") ? (
                  <ThreeModelViewer
                    modelUrl={item.src}
                    isFullscreen={isFullscreen}
                  />
                ) : (
                  <IframeWrapper
                    src={item.src}
                    label={item.label}
                    isFullscreen={isFullscreen}
                  />
                )}
              </div>
            ))}
          </Slider>
        </div>
      )}

      {/* Fullscreen Button - hide if already fullscreen */}
      {!isFullscreen && carouselSlides.length > 0 && (
        <div style={{ textAlign: "center", marginTop: "1rem" }}>
          <button onClick={handleEnterFullScreen}>Go Fullscreen</button>
        </div>
      )}

      {/* Snapshot thumbnails */}
      {snapshots.length > 0 && (
        <div
          className="thumbnail-row"
          style={{
            marginTop: "2rem",
            display: "flex",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          {snapshots.map((snap, idx) => (
            <img
              key={idx}
              src={snap}
              alt={`Snapshot ${idx + 1}`}
              style={{
                maxWidth: "120px",
                maxHeight: "80px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={() => openLightbox(idx)}
            />
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && snapshots.length > 0 && (
        <Lightbox
          mainSrc={snapshots[photoIndex]}
          nextSrc={snapshots[(photoIndex + 1) % snapshots.length]}
          prevSrc={
            snapshots[(photoIndex + snapshots.length - 1) % snapshots.length]
          }
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={prevSnapshot}
          onMoveNextRequest={nextSnapshot}
        />
      )}
    </div>
  );
};

export default Post;
